import { GanttTheme } from '../types/gantt';

export const GANTT_THEMES: Record<string, GanttTheme> = {
  'office-blue': {
    id: 'office-blue',
    name: 'Microsoft 365 Blue',
    primary: '#0078D4',
    secondary: '#2B88D8',
    accent: '#005A9E',
    background: '#FFFFFF',
    surface: '#F3F2F1',
    text: '#201F1E',
    textMuted: '#605E5C',
    grid: '#EDEBE9',
    headerBg: '#0078D4',
    headerText: '#FFFFFF',
    todayLine: '#D83B01',
    barRadius: 6,
  },
  'slate-modern': {
    id: 'slate-modern',
    name: 'Slate & Indigo',
    primary: '#4F46E5',
    secondary: '#6366F1',
    accent: '#3730A3',
    background: '#FFFFFF',
    surface: '#F8FAFC',
    text: '#0F172A',
    textMuted: '#64748B',
    grid: '#E2E8F0',
    headerBg: '#4F46E5',
    headerText: '#FFFFFF',
    todayLine: '#EF4444',
    barRadius: 6,
  },
  'emerald': {
    id: 'emerald',
    name: 'Emerald Green',
    primary: '#059669',
    secondary: '#10B981',
    accent: '#047857',
    background: '#FFFFFF',
    surface: '#ECFDF5',
    text: '#064E3B',
    textMuted: '#047857',
    grid: '#D1FAE5',
    headerBg: '#059669',
    headerText: '#FFFFFF',
    todayLine: '#DC2626',
    barRadius: 6,
  },
  'sunset': {
    id: 'sunset',
    name: 'Sunset Amber',
    primary: '#D97706',
    secondary: '#F59E0B',
    accent: '#92400E',
    background: '#FFFFFF',
    surface: '#FFFBEB',
    text: '#451A03',
    textMuted: '#92400E',
    grid: '#FEF3C7',
    headerBg: '#D97706',
    headerText: '#FFFFFF',
    todayLine: '#B91C1C',
    barRadius: 6,
  },
  'dark-mode': {
    id: 'dark-mode',
    name: 'Dark Presentation',
    primary: '#0284C7',
    secondary: '#38BDF8',
    accent: '#0369A1',
    background: '#0F172A',
    surface: '#1E293B',
    text: '#F8FAFC',
    textMuted: '#94A3B8',
    grid: '#334155',
    headerBg: '#0284C7',
    headerText: '#FFFFFF',
    todayLine: '#F43F5E',
    barRadius: 6,
  },
};

export function getTheme(themeId: string, customColor?: string): GanttTheme {
  if (themeId === 'custom' && customColor) {
    return createCustomTheme(customColor);
  }
  return GANTT_THEMES[themeId] || GANTT_THEMES['office-blue'];
}

export function createCustomTheme(colorHex: string): GanttTheme {
  const hex = colorHex.startsWith('#') ? colorHex : `#${colorHex}`;
  const isLight = isLightColor(hex);
  return {
    id: 'custom',
    name: 'Custom',
    primary: hex,
    secondary: hex,
    accent: hex,
    background: '#FFFFFF',
    surface: '#F8FAFC',
    text: '#201F1E',
    textMuted: '#605E5C',
    grid: '#EDEBE9',
    headerBg: hex,
    headerText: isLight ? '#201F1E' : '#FFFFFF',
    todayLine: '#D83B01',
    barRadius: 6,
  };
}

export function isLightColor(colorHex?: string): boolean {
  if (!colorHex) return false;
  const clean = colorHex.replace('#', '').trim();
  if (clean.length !== 6 && clean.length !== 3) return false;
  const r = parseInt(clean.length === 3 ? clean[0] + clean[0] : clean.substring(0, 2), 16);
  const g = parseInt(clean.length === 3 ? clean[1] + clean[1] : clean.substring(2, 4), 16);
  const b = parseInt(clean.length === 3 ? clean[2] + clean[2] : clean.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return false;
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  // Threshold 145: dark preset themes are all <= 135.4 (white text),
  // while light hues (light blue, sky, coral, yellow, mint, white) are >= 145 (dark text).
  return yiq >= 145;
}
